/**
 * Privacy Policy page — token-kit.com (developer-facing).
 */

import React, { useEffect } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Shield } from 'lucide-react';

export default function PrivacyPolicyPage() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="auth-page">
      <Navbar />
      <div className="container" style={{ maxWidth: 800, padding: '48px 24px', marginTop: 64 }}>
        <div className="page-header" style={{ marginBottom: 32 }}>
          <div>
            <h1><Shield size={22} style={{ verticalAlign: 'text-bottom', marginRight: 8 }} />Privacy Policy</h1>
            <p style={{ color: 'var(--text-dim)', marginTop: 4 }}>Last updated: March 22, 2026</p>
          </div>
        </div>

        <div className="legal-content" style={{ color: 'var(--text)', lineHeight: 1.8, fontSize: '0.95rem' }}>

          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>1. Introduction</h2>
            <p>CodeTunez Studios ("we", "us", "our") operates the token-kit platform, including the website token-kit.com, associated APIs, SDKs, and related services (collectively, the "Service"). This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you access or use our Service.</p>
            <p style={{ marginTop: 12 }}>By using the Service, you consent to the data practices described in this policy. If you do not agree, you must discontinue use of the Service immediately.</p>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>2. Information We Collect</h2>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginTop: 16, marginBottom: 8 }}>2.1 Information You Provide</h3>
            <ul style={{ paddingLeft: 24, marginTop: 8 }}>
              <li>Account registration information (name, email address, password)</li>
              <li>Developer profile information (company name, application details)</li>
              <li>API keys and token identifiers generated through the Service</li>
              <li>Communications you send to us (support requests, feedback)</li>
            </ul>

            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginTop: 16, marginBottom: 8 }}>2.2 Information Collected Automatically</h3>
            <ul style={{ paddingLeft: 24, marginTop: 8 }}>
              <li>IP address, browser type, operating system, and device information</li>
              <li>Usage data including API call logs, request timestamps, model usage, and token consumption</li>
              <li>Cookies and similar tracking technologies for session management</li>
              <li>Log data from our servers (access logs, error logs)</li>
            </ul>

            <h3 style={{ fontSize: '0.95rem', fontWeight: 600, marginTop: 16, marginBottom: 8 }}>2.3 Information We Do Not Collect</h3>
            <ul style={{ paddingLeft: 24, marginTop: 8 }}>
              <li>We do not store the content of LLM prompts or responses after request processing is complete</li>
              <li>We do not retain raw API keys or user tokens — only cryptographic hashes are stored</li>
              <li>We do not collect payment card numbers — payment processing is handled by third-party processors</li>
            </ul>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>3. How We Use Your Information</h2>
            <p>We use the information we collect to:</p>
            <ul style={{ paddingLeft: 24, marginTop: 8 }}>
              <li>Provide, operate, and maintain the Service</li>
              <li>Authenticate your identity and authorise access to the Service</li>
              <li>Process transactions and manage your token balance</li>
              <li>Monitor usage for billing, rate limiting, and abuse prevention</li>
              <li>Send transactional communications (account verification, security alerts)</li>
              <li>Improve and develop the Service</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>4. Data Sharing and Disclosure</h2>
            <p>We do not sell your personal information. We may share your information only in the following circumstances:</p>
            <ul style={{ paddingLeft: 24, marginTop: 8 }}>
              <li><strong>Service Providers:</strong> Third-party vendors who perform services on our behalf (cloud hosting, payment processing, analytics), bound by confidentiality obligations.</li>
              <li><strong>LLM Providers:</strong> When you make an LLM request, prompt data is transmitted to the relevant AI model provider (e.g., OpenAI, Anthropic, Amazon Bedrock) according to their respective privacy policies. We do not control how these providers process your data.</li>
              <li><strong>Legal Requirements:</strong> When required by law, subpoena, court order, or governmental regulation.</li>
              <li><strong>Protection of Rights:</strong> When we believe disclosure is necessary to protect our rights, your safety, or the safety of others, investigate fraud, or respond to a government request.</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, reorganisation, or sale of assets, your information may be transferred as a business asset.</li>
            </ul>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>5. Data Retention</h2>
            <p>We retain your personal information for as long as your account is active or as needed to provide the Service. Usage logs are retained for up to 90 days for billing and debugging purposes. We may retain certain information as required by law or for legitimate business purposes after account deletion.</p>
            <p style={{ marginTop: 12 }}>Hashed API keys and token identifiers associated with deleted accounts are permanently removed within 30 days of account deletion.</p>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>6. Data Security</h2>
            <p>We implement industry-standard security measures to protect your data, including:</p>
            <ul style={{ paddingLeft: 24, marginTop: 8 }}>
              <li>Encryption in transit (TLS 1.2+) for all API and web communications</li>
              <li>Encryption at rest for all stored data</li>
              <li>SHA-256 hashing of API keys and user tokens (raw values are never stored)</li>
              <li>Bcrypt hashing of passwords</li>
              <li>Least-privilege access controls for infrastructure</li>
            </ul>
            <p style={{ marginTop: 12 }}>No method of electronic transmission or storage is 100% secure. While we strive to use commercially acceptable means to protect your information, we cannot guarantee absolute security.</p>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>7. Your Rights</h2>
            <p>Depending on your jurisdiction, you may have the following rights:</p>
            <ul style={{ paddingLeft: 24, marginTop: 8 }}>
              <li>Access, correct, or delete your personal information</li>
              <li>Object to or restrict certain processing of your data</li>
              <li>Data portability (receive your data in a structured, machine-readable format)</li>
              <li>Withdraw consent at any time where processing is based on consent</li>
            </ul>
            <p style={{ marginTop: 12 }}>To exercise any of these rights, contact us at <a href="mailto:privacy@codetunezstudios.com" style={{ color: 'var(--accent)' }}>privacy@codetunezstudios.com</a>. We will respond within 30 days.</p>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>8. International Data Transfers</h2>
            <p>The Service is hosted on Amazon Web Services (AWS) infrastructure. Your data may be processed in the United States or other jurisdictions where AWS operates. By using the Service, you consent to the transfer of your data to jurisdictions that may have different data protection laws than your country of residence.</p>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>9. Children's Privacy</h2>
            <p>The Service is not directed to individuals under the age of 16. We do not knowingly collect personal information from children. If we become aware that a child under 16 has provided us with personal information, we will take steps to delete such information promptly.</p>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>10. Cookies</h2>
            <p>We use essential cookies for authentication and session management. We do not use advertising or third-party tracking cookies. You may disable cookies in your browser settings, but this may impair the functionality of the Service.</p>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>11. Changes to This Policy</h2>
            <p>We reserve the right to update this Privacy Policy at any time. We will notify you of material changes by posting the updated policy on this page with a revised "Last updated" date. Your continued use of the Service after any changes constitutes acceptance of the updated policy.</p>
          </section>

          <section style={{ marginBottom: 32 }}>
            <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: 12 }}>12. Contact Us</h2>
            <p>If you have questions about this Privacy Policy, please contact us at:</p>
            <p style={{ marginTop: 8 }}>
              CodeTunez Studios<br />
              Email: <a href="mailto:privacy@codetunezstudios.com" style={{ color: 'var(--accent)' }}>privacy@codetunezstudios.com</a>
            </p>
          </section>

        </div>
      </div>
      <Footer />
    </div>
  );
}
